import { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Eye,
  ListChecks,
  ClipboardCheck,
  LayoutGrid,
  FileText,
  Files,
  ShieldCheck,
  CircleQuestionMark,
  Settings,
  ChevronDown,
  Plus,
  Lock,
  ChevronsUpDown,
  Check,
  LogOut,
} from 'lucide-react';
import { useProject } from '@/lib/ProjectContext';
import { useDevMode } from '@/lib/DevModeContext';
import { useSubmissionFocus } from '@/lib/SubmissionFocusContext';
import { Telemetry } from '@/lib/telemetry';
import { useAuth } from '@/lib/AuthContext';

interface NavChild {
  to: string;
  label: string;
  badgeKey?: string; // key used to look up dynamic badge count
  gated?: boolean;   // hidden behind dev mode toggle
  /**
   * Match the route exactly. Required when one child's path is a prefix of
   * another's (e.g., `/plan` is a prefix of `/plan/sections` so it would
   * stay highlighted on every Plan tab without `end: true`).
   */
  end?: boolean;
  /**
   * Overview-entry glyph (A2F-1262). When set, the child renders a small
   * charcoal icon in place of the indent — marks a section *index* (e.g. the
   * Review Summary) as distinct from the feature pages, entirely off the
   * findings-orange scale so it never reads as an alert.
   */
  overviewIcon?: React.ComponentType<{ className?: string }>;
}

interface NavSection {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to?: string;
  children?: NavChild[];
  gated?: boolean;   // hidden behind dev mode toggle
}

const navSections: NavSection[] = [
  // A2F-1262: the Chrona Dashboard is retired — the Review Summary is the
  // default landing (reached via the "Summary" item under Chrona Review).
  {
    label: 'Chrona See',
    icon: Eye,
    children: [
      { to: '/see/explorer', label: 'Submission Explorer' },
      { to: '/see/viewer', label: 'Submission Viewer' },
    ],
  },
  {
    label: 'Chrona Plan',
    icon: ListChecks,
    children: [
      { to: '/plan', label: 'Gantt Chart', end: true },
      { to: '/plan/sections', label: 'Section Map' },
    ],
  },
  {
    label: 'Chrona Documents',
    icon: Files,
    children: [
      { to: '/documents/signoff', label: 'Signoff' },
      { to: '/documents/clearance', label: 'Clearance' },
    ],
  },
  {
    label: 'Chrona Review',
    icon: ClipboardCheck,
    children: [
      // A2F-1262: the section index — first, with an overview glyph (off the
      // orange scale) so it reads as the Review overview, not a feature page.
      { to: '/review/summary', label: 'Summary', overviewIcon: LayoutGrid, end: true },
      // Feature pages, alphabetical. The generic Reconciliation tab was retired
      // (A2F-1342) in favour of the entity-centric Study/Substance Profiles.
      // Contacts (sponsor-consistency) is hidden from the nav per product;
      // its route + component are retained. Population was fully retired
      // (A2F-1445) — no route, no nav entry, no backend service.
      { to: '/review/abbreviations', label: 'Abbreviations' },
      { to: '/review/cross-references', label: 'Cross-References' },
      { to: '/review/dose-unit', label: 'Dose/Unit' },
      { to: '/review/rename-consistency', label: 'Renames' },
      // A2F-1361/1380/1445: the merged Clinical view (design + enrollment +
      // study population figures). Un-gated — corpus-verified against real INDs.
      { to: '/review/study-profile', label: 'Study Profile' },
      // A2F-1363/A2F-1361: dev-only until the Substance Profile is corpus-verified.
      { to: '/review/substance-profile', label: 'Substance Profile', gated: true },
    ],
  },
  {
    label: 'Chrona Compliance',
    icon: ShieldCheck,
    children: [
      { to: '/compliance/readiness-snapshots', label: 'Readiness Snapshots' },
      { to: '/compliance/audit-report', label: 'Audit Report' },
    ],
  },
  {
    label: 'Help',
    icon: CircleQuestionMark,
    to: '/help',
  },
  {
    label: 'Settings',
    icon: Settings,
    children: [
      { to: '/settings/project', label: 'Manage project' },
      { to: '/settings/conflicts', label: 'Manage conflicts', badgeKey: 'conflicts' },
      { to: '/settings/users', label: 'Manage users' },
      { to: '/settings/app', label: 'Manage App' },
    ],
  },
];

function SidebarSection({ section, badges, devMode }: { section: NavSection; badges: Record<string, number>; devMode: boolean }) {
  const location = useLocation();
  const hasChildren = section.children && section.children.length > 0;

  // Auto-expand if any child is active
  const isChildActive = hasChildren
    ? section.children!.some((c) => location.pathname === c.to)
    : false;
  const [expanded, setExpanded] = useState(isChildActive);

  // For sections without children (direct links)
  if (!hasChildren && section.to) {
    const Icon = section.icon;

    // Gated section: show disabled row with lock icon
    if (section.gated && !devMode) {
      return (
        <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium text-slate-300 cursor-not-allowed select-none">
          <Icon className="w-4 h-4" />
          {section.label}
          <Lock className="w-3 h-3 ml-auto" />
        </div>
      );
    }

    return (
      <NavLink
        to={section.to}
        end={section.to === '/'}
        className={({ isActive }) =>
          `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${
            isActive
              ? 'bg-brand-orange text-white'
              : 'text-brand-charcoal hover:bg-orange-50'
          }`
        }
      >
        <Icon className="w-4 h-4" />
        {section.label}
      </NavLink>
    );
  }

  // Gated section with children: show disabled row with lock icon
  if (section.gated && !devMode) {
    const Icon = section.icon;
    return (
      <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium text-slate-300 cursor-not-allowed select-none">
        <Icon className="w-4 h-4" />
        {section.label}
        <Lock className="w-3 h-3 ml-auto" />
      </div>
    );
  }

  // Sections with children (collapsible)
  const Icon = section.icon;
  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${
          isChildActive
            ? 'text-brand-charcoal'
            : 'text-brand-charcoal hover:bg-orange-50'
        }`}
      >
        <Icon className="w-4 h-4" />
        <span className="flex-1 text-left">{section.label}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-slate-400 transition-transform ${
            expanded ? 'rotate-180' : ''
          }`}
        />
      </button>
      {expanded && (
        <div className="ml-[26px] mt-0.5 space-y-0.5">
          {section.children!.map((child) => {
            const badgeCount = child.badgeKey ? badges[child.badgeKey] || 0 : 0;

            // Gated child: show disabled row with lock icon
            if (child.gated && !devMode) {
              return (
                <div
                  key={child.to}
                  className="flex items-center justify-between px-3 py-1.5 rounded-md text-[13px] text-slate-300 cursor-not-allowed select-none"
                >
                  {child.label}
                  <Lock className="w-3 h-3" />
                </div>
              );
            }

            return (
              <NavLink
                key={child.to}
                to={child.to}
                end={child.end}
                className={({ isActive }) =>
                  `flex items-center justify-between px-3 py-1.5 rounded-md text-[13px] transition-colors ${
                    isActive
                      ? 'text-brand-orange font-medium'
                      : 'text-slate-500 hover:text-brand-charcoal hover:bg-orange-50'
                  }`
                }
              >
                <span className="flex items-center gap-2">
                  {child.overviewIcon && <child.overviewIcon className="w-3.5 h-3.5" />}
                  {child.label}
                </span>
                {badgeCount > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold bg-orange-100 text-brand-orange rounded-full min-w-[18px] text-center">
                    {badgeCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SwitchIndPopover() {
  const { projects, activeProject, setActiveProject } = useProject();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={popoverRef}>
      {/* Popover (opens above the button) */}
      {open && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden z-50">
          <div className="max-h-[240px] overflow-y-auto py-1">
            {projects.map((p) => (
              <button
                key={p.id}
                onClick={() => {
                  setActiveProject(p);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm transition-colors ${
                  p.id === activeProject?.id
                    ? 'bg-orange-50 text-brand-orange'
                    : 'text-brand-charcoal hover:bg-slate-50'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{p.name}</p>
                  <p className="text-xs text-slate-400">IND {p.indNumber}</p>
                </div>
                {p.id === activeProject?.id && (
                  <Check className="w-4 h-4 shrink-0 text-brand-orange" />
                )}
              </button>
            ))}
          </div>
          <div className="border-t border-slate-100">
            <button
              onClick={() => {
                setOpen(false);
                navigate('/fre');
              }}
              className="w-full text-left px-3 py-2 flex items-center gap-2 text-sm text-slate-500 hover:bg-slate-50 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add new IND
            </button>
          </div>
        </div>
      )}

      {/* Trigger button */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-orange text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors"
      >
        <ChevronsUpDown className="w-4 h-4" />
        Switch IND
      </button>
    </div>
  );
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function Sidebar() {
  const { conflictCount, activeProject } = useProject();
  const { devMode } = useDevMode();
  const { user, authRequired, logout } = useAuth();
  const { submissionFocusId, setSubmissionFocusId, focusedSubmission, submissions } = useSubmissionFocus();
  const [subPickerOpen, setSubPickerOpen] = useState(false);
  const subPickerRef = useRef<HTMLDivElement>(null);
  const badges: Record<string, number> = { conflicts: conflictCount };

  // Close submission picker on outside click
  useEffect(() => {
    if (!subPickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (subPickerRef.current && !subPickerRef.current.contains(e.target as Node)) {
        setSubPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [subPickerOpen]);

  return (
    <aside className="w-60 shrink-0 flex flex-col bg-white border-r border-slate-200">
      {/* Logo */}
      <div className="px-4 py-5 flex items-center gap-2.5">
        <img src="/chrona-logo.png" alt="Chrona" className="w-12 h-12 -m-1 object-contain" />
        <div className="flex flex-col">
          <span className="font-bold text-sm text-brand-charcoal tracking-wide">
            CHRONA
          </span>
          <span className="text-[9px] font-semibold text-brand-orange tracking-[0.15em] uppercase">
            Timely Biotech AI
          </span>
        </div>
      </div>

      {/* Active IND indicator */}
      {activeProject && (
        <div className="px-4 pb-3 -mt-2">
          <div className="px-2.5 py-1.5 bg-orange-50 rounded-md">
            <p className="text-[11px] font-semibold text-brand-orange truncate">
              IND {activeProject.indNumber}
            </p>
            <p className="text-[10px] text-slate-500 truncate">{activeProject.name}</p>
          </div>
        </div>
      )}

      {/* Global submission focus (A2F-454) */}
      {activeProject && submissions.length > 0 && (
        <div className="px-4 pb-2">
          <div className="relative" ref={subPickerRef}>
            <button
              onClick={() => setSubPickerOpen(!subPickerOpen)}
              className="w-full flex items-center justify-between px-2.5 py-1.5 bg-white border border-slate-200 rounded-md text-[11px] hover:border-brand-orange/50 transition-colors"
            >
              <span className="font-medium text-brand-charcoal truncate">
                {focusedSubmission
                  ? `Sub ${focusedSubmission.displaySequence}${focusedSubmission.submissionType ? ' · ' + focusedSubmission.submissionType : ''}`
                  : 'All Submissions'}
              </span>
              <ChevronDown className="w-3 h-3 text-slate-400 shrink-0" />
            </button>
            {subPickerOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-30 max-h-48 overflow-y-auto">
                <button
                  onClick={() => { setSubmissionFocusId(null); setSubPickerOpen(false); Telemetry.submissionFocusChanged({ submission_id: null, submission_display_sequence: null }); }}
                  className={`w-full text-left px-2.5 py-1.5 text-[11px] hover:bg-slate-50 ${!submissionFocusId ? 'bg-orange-50 text-brand-orange font-medium' : 'text-brand-charcoal'}`}
                >
                  All Submissions
                </button>
                {[...submissions].reverse().map(sub => (
                  <button
                    key={sub.id}
                    onClick={() => { setSubmissionFocusId(sub.id); setSubPickerOpen(false); Telemetry.submissionFocusChanged({ submission_id: sub.id, submission_display_sequence: sub.displaySequence }); }}
                    className={`w-full text-left px-2.5 py-1.5 text-[11px] hover:bg-slate-50 ${sub.id === submissionFocusId ? 'bg-orange-50 text-brand-orange font-medium' : 'text-brand-charcoal'}`}
                  >
                    Sub {sub.displaySequence}{sub.submissionType ? ` · ${sub.submissionType}` : ''}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {navSections.map((section) => (
          <SidebarSection key={section.label} section={section} badges={badges} devMode={devMode} />
        ))}
      </nav>

      {/* User identity + sign out (only when auth is active) */}
      {authRequired && user && (
        <div className="mx-3 px-3 py-2.5 border-t border-slate-200 flex items-center gap-2">
          {/* Avatar + name → /account (A2F-600). Logout stays a sibling
              so its click target doesn't navigate. */}
          <Link
            to="/account"
            title="View your account"
            className="flex items-center gap-2 flex-1 min-w-0 -mx-1 px-1 py-0.5 rounded hover:bg-orange-50/60 transition-colors"
          >
            <div className="w-7 h-7 rounded-full bg-orange-100 text-brand-orange text-[10px] font-bold flex items-center justify-center shrink-0">
              {getInitials(user.displayName || user.email)}
            </div>
            <span className="text-xs text-slate-600 truncate flex-1">
              {user.displayName || user.email}
            </span>
          </Link>
          <button
            onClick={logout}
            title="Sign out"
            className="text-slate-400 hover:text-brand-orange transition-colors shrink-0"
          >
            <LogOut size={14} />
          </button>
        </div>
      )}

      {/* Switch IND */}
      <div className="px-3 pb-4 pt-2">
        <SwitchIndPopover />
      </div>
    </aside>
  );
}
